import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../index'

export type ConversationSource = 'mo' | 'ai_builder' | 'agent'

export interface MoConversationRow {
  id: number
  uuid: string
  short_id?: string | null
  source: ConversationSource
  title: string
  status: string
  visibility: string | null
  scope_kind: string | null
  scope_label: string | null
  message_count: number
  last_message_at: string | null
  created_at: string
  updated_at: string
  user_id: number | null
  user_uuid: string | null
  user_name: string | null
  user_email: string | null
  project_id: number
  project_uuid: string | null
  project_name: string | null
  organization_id: number | null
  organization_uuid: string | null
  organization_name: string | null
  ticket_id?: number | null
  ticket_uuid?: string | null
  ticket_title?: string | null
  forensic_summary_json?: unknown
}

export interface MoMessageRow {
  id: number
  uuid: string
  role: 'user' | 'assistant' | 'tool' | 'system'
  kind: string
  content: string
  run_uuid: string | null
  metadata_json: unknown
  created_at: string
  updated_at: string | null
  tool_name?: string | null
  tool_args?: unknown
  tool_result_summary?: string | null
  approval_status?: string | null
  approval_reason?: string | null
  tokens_in?: number | null
  tokens_out?: number | null
  cost_estimate?: number | null
  author_user_id?: number | null
  author_name?: string | null
  author_email?: string | null
  speaker_label?: string | null
  is_mo_bot?: boolean
}

export interface MoConversationsStats {
  total_threads: number
  total_messages: number
  unique_users: number
  unique_projects: number
  mo_threads: number
  builder_threads: number
  agent_threads?: number
}

export interface MoConversationsResponse {
  success: boolean
  data: MoConversationRow[]
  stats: MoConversationsStats | null
  meta: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export interface MoConversationDetailResponse {
  success: boolean
  data: {
    source: ConversationSource
    thread: MoConversationRow
    messages: MoMessageRow[]
    collaborator?: { id: number; name: string | null; email: string | null } | null
    agentSessions?: Array<{
      uuid: string
      collaborator_key: string
      status: string
      messages: MoMessageRow[]
    }>
    forensicSummary?: unknown
    truncated?: boolean
    total_message_count?: number
  }
}

export interface MoStreamResponse {
  success: boolean
  data: {
    user: { id: number; name: string | null; email: string | null } | null
    project: { id: number; name: string | null; organization_name: string | null } | null
    messages: MoMessageRow[]
    total_count?: number
    truncated?: boolean
  }
}

export interface MoConversationsParams {
  page?: number
  limit?: number
  search?: string
  source?: ConversationSource | 'all'
}

export const motionodeAiApi = createApi({
  reducerPath: 'motionodeAiApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/motionode-ai',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['MoConversations'],
  endpoints: (builder) => ({
    listMoConversations: builder.query<MoConversationsResponse, MoConversationsParams | void>({
      query: (params = {}) => ({
        url: '/conversations',
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 50,
          search: params?.search ?? '',
          source: params?.source ?? 'all',
        },
      }),
      providesTags: ['MoConversations'],
    }),
    getMoConversation: builder.query<MoConversationDetailResponse, string>({
      query: (uuid) => `/conversations/${uuid}`,
    }),
    getMoUserProjectStream: builder.query<
      MoStreamResponse,
      { userId: number; projectId: number; limit?: number }
    >({
      query: ({ userId, projectId, limit }) => ({
        url: '/stream',
        params: { userId, projectId, limit: limit ?? 500 },
      }),
    }),
  }),
})

export const {
  useListMoConversationsQuery,
  useGetMoConversationQuery,
  useGetMoUserProjectStreamQuery,
} = motionodeAiApi
